import { useEffect, useState } from "react";
import { Smartphone, Share, PlusSquare, Download, MoreVertical, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";

function isStandalone() {
  return (window.matchMedia?.("(display-mode: standalone)").matches) || window.navigator.standalone === true;
}

// Pulsante/scritta "Installa Ginius sul telefono" con istruzioni iOS/Android e installazione 1-tap dove supportata.
export function InstallPrompt({ triggerClassName = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [open, setOpen] = useState(false);
  const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setOpen(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (isStandalone()) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (_) {}
    setDeferred(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="install-prompt-trigger"
          className={`inline-flex items-center gap-2 text-brand font-semibold text-sm hover:underline ${triggerClassName}`}
        >
          <Smartphone className="w-4 h-4" /> Installa Ginius sul telefono
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="install-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand">
            <Smartphone className="w-5 h-5" /> Installa Ginius
          </DialogTitle>
          <DialogDescription>Segui i passaggi per aggiungere Ginius alla schermata Home del tuo dispositivo.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 mb-2">
          <img src="/icon-192.png" alt="Ginius" className="w-12 h-12 rounded-xl ring-1 ring-slate-200" />
          <p className="text-sm text-slate-600">Aggiungi Ginius alla schermata Home: si apre come una vera app, a tutto schermo e con un solo tap.</p>
        </div>

        {deferred && (
          <Button data-testid="install-prompt-native" onClick={install} className="w-full h-12 text-base font-bold bg-brand hover:bg-brand-dark mb-2">
            <Download className="w-5 h-5 mr-2" /> Installa ora
          </Button>
        )}

        {isIOS ? (
          <ol className="space-y-3 text-sm text-slate-700" data-testid="install-steps-ios">
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-brand shrink-0" /> Apri questa pagina in <b>Safari</b>.</li>
            <li className="flex gap-2"><Share className="w-4 h-4 mt-0.5 text-brand shrink-0" /> Tocca il pulsante <b>Condividi</b> (quadrato con freccia in alto).</li>
            <li className="flex gap-2"><PlusSquare className="w-4 h-4 mt-0.5 text-brand shrink-0" /> Scegli <b>“Aggiungi a Home”</b> e conferma.</li>
          </ol>
        ) : !deferred ? (
          <ol className="space-y-3 text-sm text-slate-700" data-testid="install-steps-android">
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-brand shrink-0" /> Apri questa pagina in <b>Chrome</b> (Android) o Edge/Chrome (desktop).</li>
            <li className="flex gap-2"><MoreVertical className="w-4 h-4 mt-0.5 text-brand shrink-0" /> Apri il menu <b>⋮</b> in alto a destra.</li>
            <li className="flex gap-2"><Download className="w-4 h-4 mt-0.5 text-brand shrink-0" /> Scegli <b>“Installa app”</b> o <b>“Aggiungi a schermata Home”</b>.</li>
          </ol>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
